/**
 * Payment Service
 * Handles HFN Donation Service API calls and Razorpay integration.
 */

import { serviceErrorResult } from "./service-utils";
import { requireEnv } from "@/lib/env";
import { getUserAccessToken } from "@/lib/hfnauth";
import { logger } from "@/lib/logger";

interface RazorpayInstance {
    open: () => void;
    on: (event: string, handler: (response: unknown) => void) => void;
}

interface RazorpayConstructor {
    new (options: RazorpayOptions): RazorpayInstance;
}

interface RazorpayWindow extends Window {
    Razorpay?: RazorpayConstructor;
}

interface RazorpayOptions {
    key: string;
    amount: number;
    currency: string;
    name: string;
    description: string;
    order_id: string;
    prefill: {
        name: string;
        email: string;
        contact: string;
    };
    notes: {
        origin: string;
    };
    theme: {
        color: string;
    };
    image?: string;
    retry?: { enabled: boolean };
    handler: (_response: unknown) => void;
    modal: {
        ondismiss: () => void;
    };
}

export interface DonationRequest {
    userProfile: {
        firstName: string;
        lastName?: string;
        emailAddress: string;
        phoneNumber: string;
        addressLine1: string;
        addressLine2?: string;
        city: string;
        state: string;
        postalCode: string;
        country: string;
        citizenshipCountry: string;
    };
    lineItems: {
        donationItem: { id: string };
        currency: string;
        amount: number;
        extras: {
            donorCount: string;
            donorName: string;
            donorEmailAddress: string;
            donorPhoneNumber: string;
            donationType: string;
            isGift?: boolean;
            donationEntryPoint?: string;
            projectId?: string;
            speciesId?: string;
            attributeId?: string;
            donationReceivedFrom: string;
            reservationToken?: string;
            giftReceiverName?: string;
            giftReceiverEmailAddress?: string;
            giftReceiverPhoneNumber?: string;
            giftMessage?: string;
            displayOnDonorsList?: string;
            forestServiceSource?: string;
        };
    }[];
    clientSuccessRedirectUrl: string;
    clientFailureRedirectUrl: string;
    clientId: string;
}

export interface DonationResponse {
    donation: {
        id: string;
        status: string;
        currency: string;
        amount: number;
        payment: {
            donationReferenceNumber: string;
            trackingId: string;
            paymentStatus: string;
        };
    };
    paymentGatewayRequestParamMap: {
        id: string; // Razorpay order_id
        amount_due: number; // Amount in paise
        currency: string;
        client_id: string; // Razorpay key (comes from API response)
    };
}

export interface DonationErrorResponse {
    success: false;
    error: string;
    message: string;
    status?: number;
}

export type InitiateDonationResponse = DonationResponse | DonationErrorResponse;

export function isDonationErrorResponse(
    response: InitiateDonationResponse
): response is DonationErrorResponse {
    return "success" in response && response.success === false;
}

/**
 * Call HFN Donation Service to initiate payment.
 */
export async function initiateDonation(
    request: DonationRequest
): Promise<InitiateDonationResponse> {
    // Fallback to QA URL if env var is missing (matches pattern used for payments service URL).
    // Prevents fetch from resolving to a relative URL like "undefined/donations/donate"
    // when the build env didn't have NEXT_PUBLIC_HFN_DONATION_SERVICE_URL set.
    const donationServiceUrl = requireEnv(
        "NEXT_PUBLIC_HFN_DONATION_SERVICE_URL",
        process.env.NEXT_PUBLIC_HFN_DONATION_SERVICE_URL,
    );

    try {
        const response = await fetch(
            `${donationServiceUrl}/donations/donate`,
            {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(request),
            }
        );

        if (!response.ok) {
            logger.error("Donation service request failed", response.status);
            let message = `Unable to initiate payment. Status ${response.status}`;
            try {
                const payload = (await response.json()) as { message?: string };
                if (payload?.message) message = payload.message;
            } catch {
                // keep fallback message when non-JSON error payload is returned
            }

            return {
                success: false,
                error: "donation_request_failed",
                message,
                status: response.status,
            };
        }

        return (await response.json()) as DonationResponse;
    } catch (error) {
        return serviceErrorResult("Error calling donation service:", error, {
            success: false,
            error: "network_error",
            message: "Unable to connect to the payment service. Please try again.",
        });
    }
}

/**
 * Load Razorpay script dynamically.
 */
export function loadRazorpayScript(): Promise<boolean> {
    return new Promise((resolve) => {
        // Check if already loaded
        const typedWindow = window as RazorpayWindow;
        if (typedWindow.Razorpay) {
            resolve(true);
            return;
        }

        const script = document.createElement("script");
        script.src = "https://checkout.razorpay.com/v1/checkout.js";
        script.onload = () => {
            resolve(true);
        };
        script.onerror = () => {
            logger.error("Failed to load Razorpay script");
            resolve(false);
        };
        document.body.appendChild(script);
    });
}

/**
 * Open Razorpay checkout with payment details from API response.
 *
 * Uses `handler` mode so we can control the exact order of operations on success:
 *   1. link-payment  →  reservation gets transaction_id = pay_xxx   (AWAITED)
 *   2. redirectAck   →  Payments Service confirms → Donation Service → Pub/Sub published  (fire-and-forget, keepalive)
 *   3. navigate      →  redirect to success page
 *
 * Step 1 MUST complete before step 2 fires. Because by the time the Pub/Sub consumer
 * receives the message (step 2 + network latency + GCP Pub/Sub delivery ≈ several hundred ms),
 * link-payment has already been called and _is_our_donation() will return True.
 *
 * If we used callback_url instead, the Payments Service redirects AFTER publishing Pub/Sub,
 * meaning link-payment is always called AFTER Pub/Sub — the backend consumer would ACK
 * and discard the message before the success page even loads.
 */
function showProcessingOverlay(message: string) {
    if (document.getElementById("fbh-processing")) return;
    const el = document.createElement("div");
    el.id = "fbh-processing";
    el.setAttribute("role", "status");
    el.setAttribute("aria-live", "polite");
    el.textContent = message;
    Object.assign(el.style, {
        position: "fixed",
        inset: "0",
        zIndex: "2147483647",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px",
        textAlign: "center",
        background: "rgba(255, 255, 255, 0.92)",
        color: "#003399",
        font: "600 18px/1.4 sans-serif",
    });
    document.body.appendChild(el);
}

export function openRazorpayCheckout(
    razorpayKeyId: string, // client_id from paymentGatewayRequestParamMap
    razorpayOrderId: string, // id from paymentGatewayRequestParamMap
    amount: number, // amount_due from paymentGatewayRequestParamMap (in paise)
    currency: string,
    donationReferenceNumber: string,
    userEmail: string,
    userPhone: string,
    userName: string,
    successUrl: string,
    failureUrl: string,
    reservationToken?: string
) {
    const paymentsServiceUrl = requireEnv(
        "NEXT_PUBLIC_HFN_PAYMENTS_SERVICE_URL",
        process.env.NEXT_PUBLIC_HFN_PAYMENTS_SERVICE_URL,
    );

    /**
     * Link the Razorpay payment ID to the reservation in Django.
     * MUST be awaited before calling redirectAck (which triggers Pub/Sub publication).
     */
    async function linkPaymentEarly(paymentId: string) {
        if (!reservationToken || !paymentId) return;
        const djangoApiUrl = process.env.NEXT_PUBLIC_DJANGO_API_URL;
        if (!djangoApiUrl) return;
        try {
            const accessToken = await getUserAccessToken();
            await fetch(`${djangoApiUrl}/api/allocations/reservations/link-payment/`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
                },
                body: JSON.stringify({
                    reservation_token: reservationToken,
                    transaction_id: paymentId,
                }),
            });
        } catch {
            // Non-fatal — success page will attempt link-payment again as fallback
        }
    }

    /**
     * Called when the payment fails, or the user closes the Razorpay modal without paying.
     * Notifies Payments Service (which updates Donation Service), then redirects to our failure page.
     * Also releases the tree reservation immediately.
     */
    let failureHandled = false;
    async function cancelAndRedirectToFailure() {
        if (failureHandled) return;
        failureHandled = true;
        showProcessingOverlay("Processing, please wait…");
        // Release the held trees. Our backend also records the failed attempt here,
        // so it shows in the donor's history.
        let cancelDone: Promise<unknown> = Promise.resolve();
        if (reservationToken) {
            cancelDone = import("./tree-reservations")
                .then(({ cancelReservation }) => cancelReservation(reservationToken))
                .catch(() => {});
        }

        // Notify Payments Service of the failure (it updates the Donation Service).
        // 5s timeout guards against a hanging request; we fall back to redirecting ourselves.
        try {
            await Promise.race([
                fetch(`${paymentsServiceUrl}/open/razorpay/redirectAck`, {
                    method: "POST",
                    headers: { "Content-Type": "application/x-www-form-urlencoded" },
                    body: new URLSearchParams({
                        error: JSON.stringify({
                            description: "Payment was unsuccessful. If amount was deducted, it will be refunded within 5-7 working days.",
                            metadata: { order_id: razorpayOrderId },
                        }),
                        amount: String(amount),
                        currency: currency,
                    }).toString(),
                }),
                new Promise<never>((_, reject) =>
                    setTimeout(() => reject(new Error("redirectAck timeout")), 5000)
                ),
            ]);
        } catch {
            // best effort — the donor is redirected either way
        }
        // Give the cancel a moment to finish; navigating away would abort it.
        await Promise.race([cancelDone, new Promise((resolve) => setTimeout(resolve, 3000))]);
        // fetch() can't follow the redirect for the page, so navigate ourselves.
        window.location.href = `${failureUrl}?ref=${donationReferenceNumber}`;
    }

    const options = {
        key: razorpayKeyId,
        amount: amount,
        currency: currency,
        name: "Forests by Heartfulness",
        description: "Tree Plantation Donation",
        image: `${window.location.origin}/images/fbh_logo.png`,
        // Retry must be disabled per the Payments Service integration.
        retry: { enabled: false },
        order_id: razorpayOrderId,
        prefill: {
            name: userName,
            email: userEmail,
            contact: userPhone,
        },
        notes: {
            origin: "payments_service",
        },
        theme: {
            color: "#003399",
        },
        handler: async function (response: unknown) {
            const razorpayResponse = response as {
                razorpay_payment_id?: string;
                razorpay_order_id?: string;
                razorpay_signature?: string;
            };
            const paymentId = razorpayResponse.razorpay_payment_id || "";
            const razorpayOrderIdFromResponse = razorpayResponse.razorpay_order_id || razorpayOrderId;
            const signature = razorpayResponse.razorpay_signature || "";

            showProcessingOverlay("Confirming your payment, please wait…");

            // Step 1: Link reservation to this payment ID — AWAITED.
            // This MUST complete before step 2 fires so that _is_our_donation()
            // on the Pub/Sub consumer returns True when the message arrives.
            await linkPaymentEarly(paymentId);

            // Step 2: Notify Payments Service → Donation Service → Pub/Sub.
            // keepalive: true keeps the request alive even after page navigation starts.
            // We do NOT await this — the server processes it independently.
            fetch(`${paymentsServiceUrl}/open/razorpay/redirectAck`, {
                method: "POST",
                keepalive: true,
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                body: new URLSearchParams({
                    razorpay_payment_id: paymentId,
                    razorpay_order_id: razorpayOrderIdFromResponse,
                    razorpay_signature: signature,
                }).toString(),
            }).catch(() => {});

            // Step 3: Navigate to success page.
            window.location.href = `${successUrl}?ref=${donationReferenceNumber}&txnId=${paymentId}`;
        },
        modal: {
            ondismiss: function () {
                void cancelAndRedirectToFailure();
            },
        },
    };

    const typedWindow = window as RazorpayWindow;
    if (!typedWindow.Razorpay) {
        return;
    }
    const razorpay = new typedWindow.Razorpay(options);
    razorpay.on("payment.failed", () => {
        void cancelAndRedirectToFailure();
    });
    razorpay.open();
}
